import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import {
  activityOccurrenceAndGroupKeys,
  activityCategoryDictionary,
  closeActivity,
  displayCancelGroupReason,
  groupQueryOptions,
  type ICancelGroupReasonOption,
  isCancelledWithReason,
  removeActivityOccurrence,
  removeOccurrenceGroup,
  cancelGroupReasonOptionSchema,
  cancelOccurrenceGroup,
} from "@/api/Activities";
import {
  activityRequirementSchema,
  getUnfulfilledRequirements,
} from "@medoma/models/activityRequirements";
import { useResumeGroup } from "./useResumeGroup";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { assignGroupToShift, unassignGroupFromShift } from "@/api/Shifts";
import { dateName } from "@/Utils/DateUtils";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { isUnfinishedOccurrence } from "@/forms/changeGroupUtils";
import { ChangeGroup } from "@/forms/ChangeGroup";
import EditGroupTimeForm from "@/forms/EditGroupTimeForm";
import { ShiftDropdownMenu } from "@/pages/commandcenter/Activities/ShiftDropdownMenu";
import { QuickActivityInDetails } from "@/pages/commandcenter/Planning/TimelineTile/QuickActivityInDetails";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  matchQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { endOfDay, isPast, startOfDay } from "date-fns";
import {
  activityOccurrenceStatusSchema,
  getGroupStatusTimestamp,
  type IGroupOfActivityOccurrence,
  type IUnfinishedActivityOccurrenceStatus,
  timeOfDayDictionary,
  timeOfDaySchema,
  groupStatusSchema,
} from "@medoma/models/activities";
import {
  calculateVisitTimes,
  isVisitOutsideShiftSpan,
  groupStatusDictionary,
} from "@medoma/models/visits";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { DialogPanel } from "@medoma/components/DialogPanel/DialogPanel";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Heading } from "@medoma/components/Heading/Heading";
import { Loading } from "@medoma/components/Loading/Loading";
import { Text } from "@medoma/components/Text/Text";
import { ActivityTitle } from "@/components/ActivityTitle/ActivityTitle";
import { AssignedShiftChip } from "@/components/Chips/AssignedShiftChip";
import DurationChip from "@/components/Chips/DurationChip";
import { calculateGroupDuration } from "@/components/Chips/calculateGroupDuration";
import { RequirementChip } from "@/components/Chips/RequirementChip";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import {
  DetailsPanelHeader,
  type NavigationButtons,
} from "@/components/DetailsPanel/Header";
import { StatusTagWithDropdown } from "@/components/StatusTagWithDropdown/StatusTagWithDropdown";
import { formattedTimeSpan } from "@/components/Time/timeUtils";
import NoResults from "@/components/NoResults/NoResults";
import { ActivityOccurrenceInGroupDropdownMenu } from "./ActivityOccurrenceInGroupDropdownMenu";
import WarningChip from "@/components/Chips/WarningChip";
import styles from "./Group.module.scss";
import { PatientHeader } from "@/components/PatientHeader/PatientHeader";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import BinIcon from "@medoma/components/icons/BinIcon";
import CalendarClockIcon from "@medoma/components/icons/CalendarClockIcon";
import CircledCrossIcon from "@medoma/components/icons/CircledCrossIcon";
import RecurrenceIcon from "@medoma/components/icons/RecurrenceIcon";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import { FormProvider, useForm } from "react-hook-form";
import Form from "@/components/Form/Form";
import TextArea from "@/components/TextArea/TextArea";

const groupActionSchema = z.enum(["editing", "removing", "cancelling"]);

export const GroupRoute = () => {
  const { groupId } = z.object({ groupId: z.string() }).parse(useParams());
  const navigate = useNavigate();

  return (
    <Group
      groupId={groupId}
      closeGroupPanel={() => navigate("..")}
      navigationButtonsInHeader={{
        closeButton: <CloseButton close={() => navigate("..")} />,
      }}
    />
  );
};

const RemoveVisitDialog = ({
  group,
  isOpen,
  onClose,
  onRemove,
  isRemoving,
}: {
  group: IGroupOfActivityOccurrence;
  isOpen: boolean;
  onClose: () => void;
  onRemove: () => void;
  isRemoving: boolean;
}) => {
  const { t } = useLingui();

  return (
    <DialogPanel isOpen={isOpen} onClose={onClose} title={t`Ta bort besök`}>
      <div className={styles.removeVisitContent}>
        <Text element="div" size="base">
          <Trans>Ta bort aktivitetstillfällen</Trans>
        </Text>
        <ul className={styles.occurrenceList}>
          {group.occurrences.map((occurrence) => (
            <li key={occurrence.id} className={styles.occurrenceLabel}>
              <CategoryIcon category={occurrence.category} size="small" />
              <Text element="span" size="small" weight="medium">
                {occurrence.title}
              </Text>
              <div className={styles.separator} />
              <Text element="span" size="small">
                {occurrence.timeOfDay === timeOfDaySchema.enum.Specific
                  ? formattedTimeSpan(occurrence.start, occurrence.end)
                  : t(timeOfDayDictionary.Any.short)}
              </Text>
              <div className={styles.separator} />
              <Text element="span" size="small">
                {dateName(occurrence.start)}
              </Text>
            </li>
          ))}
        </ul>
        <Text element="p">
          <Trans>
            För återkommande aktiviteter tas endast tillfället i detta besök
            bort, inte hela serien.
          </Trans>
        </Text>
      </div>
      <div className={styles.removeVisitActions}>
        <FilledButton onClick={onRemove} disabled={isRemoving}>
          <Trans>Ta bort</Trans>
        </FilledButton>
      </div>
    </DialogPanel>
  );
};

type ICancelVisitFormData = {
  selectableCancellationReason: ICancelGroupReasonOption | undefined;
  customCancellationReason: string;
};

const CancelGroupForm = ({
  onCancelVisit,
  isCancelling,
}: {
  onCancelVisit: (reason: string) => void;
  isCancelling: boolean;
}) => {
  const { t } = useLingui();
  const methods = useForm<ICancelVisitFormData>({
    defaultValues: {
      selectableCancellationReason: undefined,
      customCancellationReason: "",
    },
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = methods;
  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const selectableCancellationReason = watch("selectableCancellationReason");

  const onSubmit = handleSubmit((formData) => {
    switch (formData.selectableCancellationReason) {
      case cancelGroupReasonOptionSchema.enum.NotHome:
      case cancelGroupReasonOptionSchema.enum.Hospitalized:
        onCancelVisit(formData.selectableCancellationReason);
        return;
      case cancelGroupReasonOptionSchema.enum.Other:
        onCancelVisit(formData.customCancellationReason.trim());
        return;
    }
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit}>
        <RadioButtons
          legend={t`Anledning till avbokning`}
          orientation="horizontal"
          errorMessage={errors.selectableCancellationReason?.message}
        >
          <RadioButton
            label={{ text: t`Ej hemma` }}
            {...register("selectableCancellationReason", {
              required: t`Välj en anledning`,
            })}
            value={cancelGroupReasonOptionSchema.enum.NotHome}
          />
          <RadioButton
            label={{ text: t`Inlagd` }}
            {...register("selectableCancellationReason", {
              required: t`Välj en anledning`,
            })}
            value={cancelGroupReasonOptionSchema.enum.Hospitalized}
          />
          <RadioButton
            label={{ text: t`Övrigt` }}
            {...register("selectableCancellationReason", {
              required: t`Välj en anledning`,
            })}
            value={cancelGroupReasonOptionSchema.enum.Other}
          />
        </RadioButtons>
        {selectableCancellationReason ===
        cancelGroupReasonOptionSchema.enum.Other ? (
          <TextArea
            label={t`Beskriv anledning`}
            showOptionalLabel={false}
            errorMessage={errors.customCancellationReason?.message}
            {...register("customCancellationReason", {
              validate: (value) =>
                value.trim().length > 0 || t`Anledning krävs`,
            })}
          />
        ) : null}
        <FilledButton type="submit" disabled={isCancelling}>
          <Trans>Avboka besök</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};

type IUnfinishedOccurrenceForChangeGroup = Pick<
  IGroupOfActivityOccurrence["occurrences"][number],
  "activityId" | "id" | "start" | "end" | "patient"
> & { status: IUnfinishedActivityOccurrenceStatus };

const GroupWarningChips = ({
  unfulfilledRequirements,
  isFinished,
  isCancelled,
  isUnplanned,
  hasExpiredActivities,
  isOutsideShiftSpan,
}: {
  unfulfilledRequirements: string[];
  isFinished: boolean;
  isCancelled: boolean;
  isUnplanned: boolean;
  hasExpiredActivities: boolean;
  isOutsideShiftSpan: boolean;
}) => {
  const hasDoubleStaffingUnfulfilled = unfulfilledRequirements.includes(
    activityRequirementSchema.enum.DoubleStaffing,
  );
  const otherUnfulfilled = unfulfilledRequirements.filter(
    (requirement) =>
      requirement !== activityRequirementSchema.enum.DoubleStaffing,
  );
  const showDoubleStaffingWarning = hasDoubleStaffingUnfulfilled && !isFinished;
  const showUnfulfilledRequirementsWarning =
    otherUnfulfilled.length > 0 && !isFinished && !isCancelled && !isUnplanned;
  const showExpiredActivitiesWarning = hasExpiredActivities && !isCancelled;
  const showOutsideShiftSpanWarning = isOutsideShiftSpan && !isFinished;

  if (
    !showDoubleStaffingWarning &&
    !showUnfulfilledRequirementsWarning &&
    !showExpiredActivitiesWarning &&
    !showOutsideShiftSpanWarning
  ) {
    return null;
  }

  return (
    <div className={styles.warningChipsWrapper}>
      {showUnfulfilledRequirementsWarning ? (
        <WarningChip size="small">
          <Trans>Kompetensbehov uppfylls inte</Trans>
        </WarningChip>
      ) : null}
      {showDoubleStaffingWarning ? (
        <WarningChip size="small">
          <Trans>2 medarbetare krävs</Trans>
        </WarningChip>
      ) : null}
      {showExpiredActivitiesWarning ? (
        <WarningChip size="small">
          <Trans>Innehåller ej genomförda aktiviteter</Trans>
        </WarningChip>
      ) : null}
      {showOutsideShiftSpanWarning ? (
        <WarningChip size="small">
          <Trans>Besöket ligger utanför passets tid</Trans>
        </WarningChip>
      ) : null}
    </div>
  );
};

const GroupTimeSection = ({
  group,
  closeGroupPanel,
}: {
  group: IGroupOfActivityOccurrence;
  closeGroupPanel: () => void;
}) => {
  const { t } = useLingui();
  const { alert } = useDialog();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const [groupAction, setGroupAction] = useState<
    z.infer<typeof groupActionSchema> | false
  >(false);

  const { mutate: removeGroupMutation, isPending: isRemoving } = useMutation({
    mutationFn: () => removeOccurrenceGroup(group.id),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort besöket.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      closeGroupPanel();
    },
  });

  const { mutate: cancelGroupMutation, isPending: isCancelling } = useMutation({
    mutationFn: (reason: string) =>
      cancelOccurrenceGroup({ occurrenceGroupId: group.id, reason: reason }),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att avboka besöket.`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      setGroupAction(false);
    },
  });

  const { resumeGroup, isResumingGroup } = useResumeGroup(group.id);

  const { data: cancelVisitEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.CancelVisit,
  );

  const { start, end, isAnyTimeOfDay } = calculateVisitTimes(group);
  const hasWellDefinedTime = start && end;
  const isFinished = group.visitStatus === groupStatusSchema.enum.finished;
  const isOngoing =
    group.visitStatus === groupStatusSchema.enum.ongoing ||
    group.visitStatus === groupStatusSchema.enum.travellingTo;
  const isCancelled = group.visitStatus === groupStatusSchema.enum.cancelled;
  const canEditTime = !isFinished && !isCancelled;
  const canRemove = !isFinished && !isOngoing && !isCancelled;
  const canCancel =
    !isFinished && !isOngoing && !isCancelled && cancelVisitEnabled;
  const canResume = isCancelled && cancelVisitEnabled;

  const toggleEditTime = () => {
    const { visitStatus } = group;
    if (
      visitStatus === groupStatusSchema.enum.finished ||
      visitStatus === groupStatusSchema.enum.ongoing ||
      visitStatus === groupStatusSchema.enum.travellingTo
    ) {
      const visitStatusText = t(
        groupStatusDictionary[visitStatus],
      ).toLocaleLowerCase();
      return alert({
        title: t`Kan inte ändra tid`,
        message: `${t`Det går inte att ändra tid för ett besök som har status`} "${visitStatusText}".`,
      });
    }
    const hasOngoingOrFinishedOccurrences = group.occurrences.some(
      (occurrence) =>
        occurrence.status === activityOccurrenceStatusSchema.enum.finished ||
        occurrence.status === activityOccurrenceStatusSchema.enum.ongoing,
    );
    if (hasOngoingOrFinishedOccurrences) {
      return alert({
        title: t`Kan inte ändra tid`,
        message: t`Det går inte att ändra tid för ett besök som innehåller pågående eller avslutade aktiviteter.`,
      });
    }
    setGroupAction((prev) =>
      prev === groupActionSchema.enum.editing
        ? false
        : groupActionSchema.enum.editing,
    );
  };

  return (
    <>
      <div className={styles.timeAndDate}>
        {hasWellDefinedTime ? (
          <Text element="span">
            {isAnyTimeOfDay
              ? t(timeOfDayDictionary.Any.short)
              : formattedTimeSpan(start, end)}
            , {dateName(start)}
          </Text>
        ) : group.timeOfDay === timeOfDaySchema.enum.Any ? (
          <Text element="span">
            {t(timeOfDayDictionary.Any.short)}
            {group.start ? `, ${dateName(group.start)}` : ""}
          </Text>
        ) : (
          <NoResults message={t`--:-- (tid saknas)`} />
        )}
      </div>
      <div className={styles.actionRow}>
        {groupAction === groupActionSchema.enum.editing ? (
          <PlainButton
            size="small"
            weight="light"
            onClick={toggleEditTime}
            aria-label={t`Ändra tid`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : groupAction === groupActionSchema.enum.cancelling ? (
          <PlainButton
            size="small"
            weight="light"
            onClick={() => setGroupAction(false)}
            disabled={isCancelling}
            aria-label={t`Avboka besök`}
          >
            <Trans>Avbryt</Trans>
          </PlainButton>
        ) : (
          <>
            {canEditTime ? (
              <PlainButton
                size="small"
                weight="light"
                onClick={toggleEditTime}
                aria-label={t`Ändra tid`}
              >
                <span className={styles.changeTimeIcon}>
                  <CalendarClockIcon />
                </span>
                <Trans>Ändra</Trans>
              </PlainButton>
            ) : null}
            {canCancel ? (
              <PlainButton
                size="small"
                weight="light"
                onClick={() =>
                  setGroupAction(groupActionSchema.enum.cancelling)
                }
                disabled={isCancelling}
                aria-label={t`Avboka`}
              >
                <CircledCrossIcon />
                <Trans>Avboka</Trans>
              </PlainButton>
            ) : null}
            {canResume ? (
              <PlainButton
                size="small"
                weight="light"
                onClick={() => resumeGroup()}
                disabled={isResumingGroup}
                aria-label={t`Återuppta besöket`}
              >
                <RecurrenceIcon />
                <Trans>Återuppta besöket</Trans>
              </PlainButton>
            ) : null}
            {canRemove ? (
              <PlainButton
                size="small"
                weight="light"
                onClick={() => setGroupAction(groupActionSchema.enum.removing)}
                disabled={isRemoving}
                aria-label={t`Ta bort`}
              >
                <BinIcon />
                <Trans>Ta bort</Trans>
              </PlainButton>
            ) : null}
          </>
        )}
      </div>
      {groupAction === groupActionSchema.enum.removing ? (
        // tests fails without this but rather set this check in "isOpen"
        <RemoveVisitDialog
          group={group}
          isOpen={true}
          onClose={() => setGroupAction(false)}
          onRemove={() => removeGroupMutation()}
          isRemoving={isRemoving}
        />
      ) : null}
      {groupAction === groupActionSchema.enum.cancelling ? (
        <div className={styles.editContainer}>
          <CancelGroupForm
            onCancelVisit={(reason) => cancelGroupMutation(reason)}
            isCancelling={isCancelling}
          />
        </div>
      ) : null}
      {groupAction === groupActionSchema.enum.editing ? (
        <div className={styles.editContainer}>
          <Heading level="h3" margin="bottom">
            <Trans>Ändra tid</Trans>
          </Heading>
          <EditGroupTimeForm
            groupId={group.id}
            currentTimeCategory={group.timeOfDay}
            groupStart={group.start}
            onSuccess={() => setGroupAction(false)}
          />
        </div>
      ) : null}
    </>
  );
};

const GroupStatusAndAssignees = ({
  group,
}: {
  group: IGroupOfActivityOccurrence;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const {
    mutate: unassignFromShiftMutation,
    isPending: isUnassigningFromShift,
  } = useMutation({
    mutationFn: ({ shiftId }: { shiftId: number }) =>
      unassignGroupFromShift(group.id, shiftId),
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort besöket från arbetspasset.`} ${deducedError(error)}`,
      );
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const { mutate: allocateShiftMutation, isPending: isAllocating } =
    useMutation({
      mutationFn: (shiftId: number) => assignGroupToShift(group.id, shiftId),
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att lägga till besöket på arbetspasset.`} ${deducedError(error)}`,
        );
      },
      onSuccess: () => queryClient.invalidateQueries(),
    });

  const isFinished = group.visitStatus === groupStatusSchema.enum.finished;
  const isCancelled = group.visitStatus === groupStatusSchema.enum.cancelled;
  const assignees = group.assignees;
  const unfulfilledRequirements = getUnfulfilledRequirements(group);
  const status =
    group.visitStatus ?? activityOccurrenceStatusSchema.enum.notReady;
  const canAddShiftToVisit = !isFinished && !isCancelled;
  const canRemoveShiftFromVisit = !isFinished && !isCancelled;
  const cancellationReason = isCancelledWithReason(group)
    ? displayCancelGroupReason(group.visitCancellationReason, t)
    : undefined;

  const removeShift = ({ shiftIdsToRemove }: { shiftIdsToRemove: number }) => {
    return unassignFromShiftMutation({ shiftId: shiftIdsToRemove });
  };

  return (
    <div className={styles.statusAndAssignees}>
      <div className={styles.statusTagWithReason}>
        {isCancelled ? (
          <>
            <StatusTagWithDropdown
              status={status}
              variant="icon"
              groupOrVisit={group}
            />
            <Text element="span" size="small">
              <Trans>Avbokat</Trans>
              {cancellationReason ? (
                <>
                  <span
                    className={styles.cancellationReasonSeparator}
                    aria-hidden
                  />
                  {cancellationReason}
                </>
              ) : null}
            </Text>
          </>
        ) : (
          <StatusTagWithDropdown
            status={status}
            variant={
              getGroupStatusTimestamp(group)
                ? "icon-and-text-with-timestamp"
                : "icon-and-text"
            }
            timestamp={getGroupStatusTimestamp(group)}
            groupOrVisit={group}
          />
        )}
      </div>
      <div className={styles.assignees}>
        <ul className={styles.assigneesList}>
          {assignees.map((shift) => (
            <li key={shift.id}>
              <AssignedShiftChip
                medicalCompetence={shift.competence}
                shift={shift}
                size="small"
                onRemove={
                  canRemoveShiftFromVisit
                    ? () => {
                        removeShift({
                          shiftIdsToRemove: shift.id,
                        });
                      }
                    : undefined
                }
                disabled={isUnassigningFromShift}
              />
            </li>
          ))}
          {unfulfilledRequirements.map((unfulfilledRequirement) => (
            <li key={unfulfilledRequirement}>
              <RequirementChip
                requirement={unfulfilledRequirement}
                state="unfulfilled"
                size="small"
              />
            </li>
          ))}
        </ul>
        {canAddShiftToVisit ? (
          <ShiftDropdownMenu
            assignedShiftIds={assignees.map((shift) => shift.id)}
            unfulfilledRequirements={unfulfilledRequirements}
            onAllocate={(shiftId: number) => {
              allocateShiftMutation(shiftId);
            }}
            isAllocating={isAllocating}
            shiftsFromDate={startOfDay(group.start)}
            patientTeam={"team" in group.patient ? group.patient.team : null}
          />
        ) : null}
      </div>
    </div>
  );
};

const GroupActivities = ({
  group,
  closeGroupPanel,
}: {
  group: IGroupOfActivityOccurrence;
  closeGroupPanel: () => void;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();
  const [occurrenceBeingMoved, setOccurrenceBeingMoved] =
    useState<IUnfinishedOccurrenceForChangeGroup | null>(null);

  const isFinishedVisit = group.visitStatus === groupStatusSchema.enum.finished;

  // A removed occurrence is part of shifts, routes and activity lists as well, so all of them need
  // invalidating. The visit details are the exception when the last occurrence was removed, since
  // the visit is removed along with it and can no longer be fetched.
  const invalidateAllExceptRemovedVisit = (
    removedVisitId: string | undefined,
  ) =>
    queryClient.invalidateQueries({
      predicate: (query) =>
        removedVisitId === undefined ||
        !matchQuery(
          { queryKey: activityOccurrenceAndGroupKeys.detail(removedVisitId) },
          query,
        ),
    });

  const { mutate: removeOccurrenceMutation, isPending: isRemovingOccurrence } =
    useMutation({
      mutationFn: ({
        activityId,
        occurrenceId,
      }: {
        activityId: string;
        occurrenceId: string;
      }) => removeActivityOccurrence({ activityId, occurrenceId }),
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att ta bort aktivitetstillfället`} ${deducedError(error)}`,
        );
      },
      onSuccess: async () => {
        const removedVisitId =
          group.occurrences.length === 1 ? group.id : undefined;
        await invalidateAllExceptRemovedVisit(removedVisitId);
        if (removedVisitId) {
          closeGroupPanel();
        }
      },
    });

  const { mutate: closeActivityMutation, isPending: isClosingActivity } =
    useMutation({
      mutationFn: (activityId: string) => closeActivity(activityId),
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att stänga aktiviteten.`} ${deducedError(error)}`,
        );
      },
      onSuccess: async (_, activityId) => {
        const onlyOccurrencesFromClosedActivityInGroup =
          group.occurrences.every(
            (occurrence) => occurrence.activityId === activityId,
          );
        const removedVisitId = onlyOccurrencesFromClosedActivityInGroup
          ? group.id
          : undefined;
        await invalidateAllExceptRemovedVisit(removedVisitId);
        if (removedVisitId) {
          closeGroupPanel();
        }
      },
    });

  const handleRemoveOccurrence = async (
    occurrence: IGroupOfActivityOccurrence["occurrences"][number],
  ) => {
    if (occurrence.status === activityOccurrenceStatusSchema.enum.ongoing) {
      const confirmed = await confirm({
        title: t`Ta bort aktivitet`,
        message: t`Är du säker på att du vill ta bort en aktivitet i ett pågående besök?`,
      });
      if (!confirmed) {
        return;
      }
    }
    removeOccurrenceMutation({
      activityId: occurrence.activityId,
      occurrenceId: occurrence.id,
    });
  };

  return (
    <>
      <div className={styles.activitiesHeading}>
        <Heading level="h2" size="base">
          <Trans>Aktiviteter</Trans>
        </Heading>
      </div>
      <ul className={styles.activitiesList}>
        {group.occurrences.map((occurrence) => {
          return (
            <li key={`${occurrence.activityId}-${occurrence.id}`}>
              <ActivityTitle
                statusTag={
                  <StatusTagWithDropdown
                    status={occurrence.status}
                    size="tiny"
                    activityOccurrence={occurrence}
                    activityOccurrenceIsPartOfFinishedVisit={isFinishedVisit}
                  />
                }
                activityOccurrence={occurrence}
                linkTo={`activities/${occurrence.activityId}/occurrences/${occurrence.id}`}
                size="small"
                showDescriptionAndComment
                showRecurringIcon
                weight="regular"
                endOfTitleRowSlot={
                  <ActivityOccurrenceInGroupDropdownMenu
                    occurrence={occurrence}
                    onRemove={handleRemoveOccurrence}
                    onCloseSeries={(activityId) =>
                      closeActivityMutation(activityId)
                    }
                    onMove={(occ) => {
                      if (isUnfinishedOccurrence(occ)) {
                        setOccurrenceBeingMoved(occ);
                      }
                    }}
                    isRemoving={isRemovingOccurrence}
                    isClosing={isClosingActivity}
                  />
                }
              />
              {occurrenceBeingMoved?.id === occurrence.id &&
              occurrenceBeingMoved?.activityId === occurrence.activityId ? (
                <div className={styles.moveSection}>
                  <PlainButton
                    size="small"
                    weight="light"
                    onClick={() => setOccurrenceBeingMoved(null)}
                  >
                    <Trans>Avbryt</Trans>
                  </PlainButton>
                  <ChangeGroup
                    activityOccurrence={occurrenceBeingMoved}
                    onSubmitSuccess={() => setOccurrenceBeingMoved(null)}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </>
  );
};

type GroupProps = {
  groupId: string;
  closeGroupPanel: () => void;
  navigationButtonsInHeader?: NavigationButtons;
};

const Group = ({
  groupId,
  closeGroupPanel,
  navigationButtonsInHeader,
}: GroupProps) => {
  const {
    data: group,
    isPending,
    isError,
    error,
  } = useQuery(groupQueryOptions(groupId));

  const { data: showDuration } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  const { t } = useLingui();

  if (isPending) {
    return <Loading message={t`Hämtar besök`} padding={24} />;
  }

  if (isError) {
    return <ErrorMessage message={deducedError(error)} padding={24} />;
  }

  const { start, end } = calculateVisitTimes(group);
  const durationMinutes = calculateGroupDuration(
    group.occurrences.map((o) => o.duration),
  );

  const isFinished = group.visitStatus === groupStatusSchema.enum.finished;
  const isCancelled = group.visitStatus === groupStatusSchema.enum.cancelled;
  const isUnplanned = !group.visitStatus;
  const unfulfilledRequirements = getUnfulfilledRequirements(group);
  const hasExpiredActivities = group.occurrences.some(
    (occurrence) =>
      occurrence.status === activityOccurrenceStatusSchema.enum.expired,
  );
  const canAddQuickActivity =
    !isFinished && !isCancelled && !isPast(endOfDay(group.end));
  const showDurationChip = !!(start && end) && showDuration;

  return (
    <article className={styles.group}>
      <DetailsPanelHeader
        icon={<CategoryIcon category={group.category} size="small" />}
        title={t(activityCategoryDictionary[group.category])}
        navigationButtons={navigationButtonsInHeader}
      />
      <GroupWarningChips
        unfulfilledRequirements={unfulfilledRequirements}
        isFinished={isFinished}
        isCancelled={isCancelled}
        isUnplanned={isUnplanned}
        hasExpiredActivities={hasExpiredActivities}
        isOutsideShiftSpan={group.assignees.some((shift) =>
          isVisitOutsideShiftSpan(group, shift),
        )}
      />
      <PatientHeader patient={group.patient} />
      <GroupTimeSection group={group} closeGroupPanel={closeGroupPanel} />
      {showDurationChip ? (
        <div className={styles.duration}>
          <DurationChip minutes={durationMinutes} size="small" />
        </div>
      ) : null}
      <GroupStatusAndAssignees group={group} />
      <GroupActivities group={group} closeGroupPanel={closeGroupPanel} />
      {canAddQuickActivity ? (
        <div className={styles.addActivityButton}>
          <QuickActivityInDetails group={group} />
        </div>
      ) : null}
    </article>
  );
};

export { Group };
